import * as z from 'zod';

export type CurrencyConfig = {
	code: string;
	symbol: string;
	name: string;
	digitalCode: number;
	template?: string;
};

export const currencySchema = z.custom<CurrencyConfig['code']>(
	(input: unknown) => typeof input === 'string' && !!currencies[input as keyof typeof currencies],
	{ message: 'The provided input is not an expected currency code' },
);

export const currencyCodes = [
	'AED',
	'AFN',
	'ALL',
	'AMD',
	'AOA',
	'ARS',
	'AUD',
	'AWG',
	'AZN',
	'BAM',
	'BBD',
	'BDT',
	'BGN',
	'BHD',
	'BIF',
	'BMD',
	'BND',
	'BOB',
	'BRL',
	'BSD',
	'BTN',
	'BWP',
	'BYR',
	'BZD',
	'CAD',
	'CDF',
	'CHF',
	'CLP',
	'CNY',
	'COP',
	'CRC',
	'CUP',
	'CVE',
	'CZK',
	'DJF',
	'DKK',
	'DOP',
	'DZD',
	'EGP',
	'ERN',
	'ETB',
	'EUR',
	'FJD',
	'FKP',
	'GBP',
	'GEL',
	'GHS',
	'GIP',
	'GMD',
	'GNF',
	'GTQ',
	'GYD',
	'HKD',
	'HNL',
	'HRK',
	'HTG',
	'HUF',
	'IDR',
	'ILS',
	'INR',
	'IQD',
	'IRR',
	'ISK',
	'JMD',
	'JOD',
	'JPY',
	'KES',
	'KGS',
	'KHR',
	'KPW',
	'KRW',
	'KWD',
	'KYD',
	'KZT',
	'LAK',
	'LBP',
	'LKR',
	'LRD',
	'LSL',
	'LYD',
	'MAD',
	'MDL',
	'MGA',
	'MKD',
	'MMK',
	'MNT',
	'MOP',
	'MRO',
	'MUR',
	'MVR',
	'MWK',
	'MXN',
	'MYR',
	'MZN',
	'NAD',
	'NGN',
	'NIO',
	'NOK',
	'NPR',
	'NZD',
	'OMR',
	'PAB',
	'PEN',
	'PGK',
	'PHP',
	'PKR',
	'PLN',
	'PYG',
	'QAR',
	'RON',
	'RSD',
	'RUB',
	'RWF',
	'SAR',
	'SBD',
	'SCR',
	'SDG',
	'SEK',
	'SGD',
	'SHP',
	'SLL',
	'SOS',
	'SRD',
	'STD',
	'SYP',
	'SZL',
	'THB',
	'TJS',
	'TMT',
	'TND',
	'TOP',
	'TRY',
	'TTD',
	'TWD',
	'TZS',
	'UAH',
	'UGX',
	'USD',
	'UYU',
	'UZS',
	'VEF',
	'VND',
	'VUV',
	'WST',
	'XAF',
	'XCD',
	'XPF',
	'YER',
	'ZAR',
	'ZMW',
	'ZWL',
] as const;

// ISO Currency codes, and their data. From https://www.kaggle.com/datasets/phanee16/currency-and-country-code-mapping.
export const currencies = {
	AED: {
		symbol: 'د.إ',
		name: 'UAE Dirham',
		digitalCode: 784,
		code: 'AED',
	},
	AFN: {
		symbol: 'Af',
		name: 'Afghani',
		digitalCode: 971,
		code: 'AFN',
	},
	ALL: {
		symbol: 'L',
		name: 'Lek',
		digitalCode: 8,
		code: 'ALL',
	},
	AMD: {
		symbol: 'Դ',
		name: 'Armenian Dram',
		digitalCode: 51,
		code: 'AMD',
	},
	AOA: {
		symbol: 'Kz',
		name: 'Kwanza',
		digitalCode: 973,
		code: 'AOA',
	},
	ARS: {
		symbol: '$',
		name: 'Argentine Peso',
		digitalCode: 32,
		code: 'ARS',
	},
	AUD: {
		symbol: '$',
		name: 'Australian Dollar',
		digitalCode: 36,
		code: 'AUD',
	},
	AWG: {
		symbol: 'ƒ',
		name: 'Aruban Guilder/Florin',
		digitalCode: 533,
		code: 'AWG',
	},
	AZN: {
		symbol: 'ман',
		name: 'Azerbaijanian Manat',
		digitalCode: 944,
		code: 'AZN',
	},
	BAM: {
		symbol: 'КМ',
		name: 'Konvertibilna Marka',
		digitalCode: 977,
		code: 'BAM',
	},
	BBD: {
		symbol: '$',
		name: 'Barbados Dollar',
		digitalCode: 52,
		code: 'BBD',
	},
	BDT: {
		symbol: '৳',
		name: 'Taka',
		digitalCode: 50,
		code: 'BDT',
	},
	BGN: {
		symbol: 'лв',
		name: 'Bulgarian Lev',
		digitalCode: 975,
		code: 'BGN',
	},
	BHD: {
		symbol: 'ب.د',
		name: 'Bahraini Dinar',
		digitalCode: 48,
		code: 'BHD',
	},
	BIF: {
		symbol: '₣',
		name: 'Burundi Franc',
		digitalCode: 108,
		code: 'BIF',
	},
	BMD: {
		symbol: '$',
		name: 'Bermudian Dollar',
		digitalCode: 60,
		code: 'BMD',
	},
	BND: {
		symbol: '$',
		name: 'Brunei Dollar',
		digitalCode: 96,
		code: 'BND',
	},
	BOB: {
		symbol: 'Bs.',
		name: 'Boliviano',
		digitalCode: 68,
		code: 'BOB',
	},
	BRL: {
		symbol: 'R$',
		name: 'Brazilian Real',
		digitalCode: 986,
		code: 'BRL',
	},
	BSD: {
		symbol: '$',
		name: 'Bahamian Dollar',
		digitalCode: 44,
		code: 'BSD',
	},
	BTN: {
		symbol: 'Nu.',
		name: 'Ngultrum',
		digitalCode: 64,
		code: 'BTN',
	},
	BWP: {
		symbol: 'P',
		name: 'Pula',
		digitalCode: 72,
		code: 'BWP',
	},
	BYR: {
		symbol: 'Br',
		name: 'Belarussian Ruble',
		digitalCode: 974,
		code: 'BYR',
	},
	BZD: {
		symbol: '$',
		name: 'Belize Dollar',
		digitalCode: 84,
		code: 'BZD',
	},
	CAD: {
		symbol: '$',
		name: 'Canadian Dollar',
		digitalCode: 124,
		code: 'CAD',
	},
	CDF: {
		symbol: '₣',
		name: 'Congolese Franc',
		digitalCode: 976,
		code: 'CDF',
	},
	CHF: {
		symbol: '₣',
		name: 'Swiss Franc',
		digitalCode: 756,
		code: 'CHF',
	},
	CLP: {
		symbol: '$',
		name: 'Chilean Peso',
		digitalCode: 152,
		code: 'CLP',
	},
	CNY: {
		symbol: '¥',
		name: 'Yuan',
		digitalCode: 156,
		code: 'CNY',
	},
	COP: {
		symbol: '$',
		name: 'Colombian Peso',
		digitalCode: 170,
		code: 'COP',
	},
	CRC: {
		symbol: '₡',
		name: 'Costa Rican Colon',
		digitalCode: 188,
		code: 'CRC',
	},
	CUP: {
		symbol: '$',
		name: 'Cuban Peso',
		digitalCode: 192,
		code: 'CUP',
	},
	CVE: {
		symbol: '$',
		name: 'Cape Verde Escudo',
		digitalCode: 132,
		code: 'CVE',
	},
	CZK: {
		symbol: 'Kč',
		name: 'Czech Koruna',
		digitalCode: 203,
		code: 'CZK',
	},
	DJF: {
		symbol: '₣',
		name: 'Djibouti Franc',
		digitalCode: 262,
		code: 'DJF',
	},
	DKK: {
		symbol: 'kr',
		name: 'Danish Krone',
		digitalCode: 208,
		code: 'DKK',
	},
	DOP: {
		symbol: '$',
		name: 'Dominican Peso',
		digitalCode: 214,
		code: 'DOP',
	},
	DZD: {
		symbol: 'د.ج',
		name: 'Algerian Dinar',
		digitalCode: 12,
		code: 'DZD',
	},
	EGP: {
		symbol: '£',
		name: 'Egyptian Pound',
		digitalCode: 818,
		code: 'EGP',
	},
	ERN: {
		symbol: 'Nfk',
		name: 'Nakfa',
		digitalCode: 232,
		code: 'ERN',
	},
	ETB: {
		symbol: '??',
		name: 'Ethiopian Birr',
		digitalCode: 230,
		code: 'ETB',
	},
	EUR: {
		symbol: '€',
		name: 'Euro',
		digitalCode: 978,
		code: 'EUR',
	},
	FJD: {
		symbol: '$',
		name: 'Fiji Dollar',
		digitalCode: 242,
		code: 'FJD',
	},
	FKP: {
		symbol: '£',
		name: 'Falkland Islands Pound',
		digitalCode: 238,
		code: 'FKP',
	},
	GBP: {
		symbol: '£',
		name: 'Pound Sterling',
		digitalCode: 826,
		code: 'GBP',
	},
	GEL: {
		symbol: 'ლ',
		name: 'Lari',
		digitalCode: 981,
		code: 'GEL',
	},
	GHS: {
		symbol: '₵',
		name: 'Cedi',
		digitalCode: 936,
		code: 'GHS',
	},
	GIP: {
		symbol: '£',
		name: 'Gibraltar Pound',
		digitalCode: 292,
		code: 'GIP',
	},
	GMD: {
		symbol: 'D',
		name: 'Dalasi',
		digitalCode: 270,
		code: 'GMD',
	},
	GNF: {
		symbol: '₣',
		name: 'Guinea Franc',
		digitalCode: 324,
		code: 'GNF',
	},
	GTQ: {
		symbol: 'Q',
		name: 'Quetzal',
		digitalCode: 320,
		code: 'GTQ',
	},
	GYD: {
		symbol: '$',
		name: 'Guyana Dollar',
		digitalCode: 328,
		code: 'GYD',
	},
	HKD: {
		symbol: '$',
		name: 'Hong Kong Dollar',
		digitalCode: 344,
		code: 'HKD',
	},
	HNL: {
		symbol: 'L',
		name: 'Lempira',
		digitalCode: 340,
		code: 'HNL',
	},
	HRK: {
		symbol: 'Kn',
		name: 'Croatian Kuna',
		digitalCode: 191,
		code: 'HRK',
	},
	HTG: {
		symbol: 'G',
		name: 'Gourde',
		digitalCode: 332,
		code: 'HTG',
	},
	HUF: {
		symbol: 'Ft',
		name: 'Forint',
		digitalCode: 348,
		code: 'HUF',
	},
	IDR: {
		symbol: 'Rp',
		name: 'Rupiah',
		digitalCode: 360,
		code: 'IDR',
	},
	ILS: {
		symbol: '₪',
		name: 'New Israeli Shekel',
		digitalCode: 376,
		code: 'ILS',
	},
	INR: {
		symbol: '₹',
		name: 'Indian Rupee',
		digitalCode: 356,
		code: 'INR',
	},
	IQD: {
		symbol: 'ع.د',
		name: 'Iraqi Dinar',
		digitalCode: 368,
		code: 'IQD',
	},
	IRR: {
		symbol: '﷼',
		name: 'Iranian Rial',
		digitalCode: 364,
		code: 'IRR',
	},
	ISK: {
		symbol: 'Kr',
		name: 'Iceland Krona',
		digitalCode: 352,
		code: 'ISK',
	},
	JMD: {
		symbol: '$',
		name: 'Jamaican Dollar',
		digitalCode: 388,
		code: 'JMD',
	},
	JOD: {
		symbol: 'د.ا',
		name: 'Jordanian Dinar',
		digitalCode: 400,
		code: 'JOD',
	},
	JPY: {
		symbol: '¥',
		name: 'Yen',
		digitalCode: 392,
		code: 'JPY',
	},
	KES: {
		symbol: 'Sh',
		name: 'Kenyan Shilling',
		digitalCode: 404,
		code: 'KES',
	},
	KGS: {
		symbol: '??',
		name: 'Som',
		digitalCode: 417,
		code: 'KGS',
	},
	KHR: {
		symbol: '៛',
		name: 'Riel',
		digitalCode: 116,
		code: 'KHR',
	},
	KPW: {
		symbol: '₩',
		name: 'North Korean Won',
		digitalCode: 408,
		code: 'KPW',
	},
	KRW: {
		symbol: '₩',
		name: 'South Korean Won',
		digitalCode: 410,
		code: 'KRW',
	},
	KWD: {
		symbol: 'د.ك',
		name: 'Kuwaiti Dinar',
		digitalCode: 414,
		code: 'KWD',
	},
	KYD: {
		symbol: '$',
		name: 'Cayman Islands Dollar',
		digitalCode: 136,
		code: 'KYD',
	},
	KZT: {
		symbol: '〒',
		name: 'Tenge',
		digitalCode: 398,
		code: 'KZT',
	},
	LAK: {
		symbol: '₭',
		name: 'Kip',
		digitalCode: 418,
		code: 'LAK',
	},
	LBP: {
		symbol: 'ل.ل',
		name: 'Lebanese Pound',
		digitalCode: 422,
		code: 'LBP',
	},
	LKR: {
		symbol: 'Rs',
		name: 'Sri Lanka Rupee',
		digitalCode: 144,
		code: 'LKR',
	},
	LRD: {
		symbol: '$',
		name: 'Liberian Dollar',
		digitalCode: 430,
		code: 'LRD',
	},
	LSL: {
		symbol: 'L',
		name: 'Loti',
		digitalCode: 426,
		code: 'LSL',
	},
	LYD: {
		symbol: 'ل.د',
		name: 'Libyan Dinar',
		digitalCode: 434,
		code: 'LYD',
	},
	MAD: {
		symbol: 'د.م.',
		name: 'Moroccan Dirham',
		digitalCode: 504,
		code: 'MAD',
	},
	MDL: {
		symbol: 'L',
		name: 'Moldavian Leu',
		digitalCode: 498,
		code: 'MDL',
	},
	MGA: {
		symbol: 'Ar',
		name: 'Malagasy Ariary',
		digitalCode: 969,
		code: 'MGA',
	},
	MKD: {
		symbol: 'ден',
		name: 'Denar',
		digitalCode: 807,
		code: 'MKD',
	},
	MMK: {
		symbol: 'K',
		name: 'Kyat',
		digitalCode: 104,
		code: 'MMK',
	},
	MNT: {
		symbol: '₮',
		name: 'Tugrik',
		digitalCode: 496,
		code: 'MNT',
	},
	MOP: {
		symbol: 'P',
		name: 'Pataca',
		digitalCode: 446,
		code: 'MOP',
	},
	MRO: {
		symbol: 'UM',
		name: 'Ouguiya',
		digitalCode: 478,
		code: 'MRO',
	},
	MUR: {
		symbol: '₨',
		name: 'Mauritius Rupee',
		digitalCode: 480,
		code: 'MUR',
	},
	MVR: {
		symbol: 'ރ.',
		name: 'Rufiyaa',
		digitalCode: 462,
		code: 'MVR',
	},
	MWK: {
		symbol: 'MK',
		name: 'Kwacha',
		digitalCode: 454,
		code: 'MWK',
	},
	MXN: {
		symbol: '$',
		name: 'Mexican Peso',
		digitalCode: 484,
		code: 'MXN',
	},
	MYR: {
		symbol: 'RM',
		name: 'Malaysian Ringgit',
		digitalCode: 458,
		code: 'MYR',
	},
	MZN: {
		symbol: 'MTn',
		name: 'Metical',
		digitalCode: 943,
		code: 'MZN',
	},
	NAD: {
		symbol: '$',
		name: 'Namibia Dollar',
		digitalCode: 516,
		code: 'NAD',
	},
	NGN: {
		symbol: '₦',
		name: 'Naira',
		digitalCode: 566,
		code: 'NGN',
	},
	NIO: {
		symbol: 'C$',
		name: 'Cordoba Oro',
		digitalCode: 558,
		code: 'NIO',
	},
	NOK: {
		symbol: 'kr',
		name: 'Norwegian Krone',
		digitalCode: 578,
		code: 'NOK',
	},
	NPR: {
		symbol: '₨',
		name: 'Nepalese Rupee',
		digitalCode: 524,
		code: 'NPR',
	},
	NZD: {
		symbol: '$',
		name: 'New Zealand Dollar',
		digitalCode: 554,
		code: 'NZD',
	},
	OMR: {
		symbol: 'ر.ع.',
		name: 'Rial Omani',
		digitalCode: 512,
		code: 'OMR',
	},
	PAB: {
		symbol: 'B/.',
		name: 'Balboa',
		digitalCode: 590,
		code: 'PAB',
	},
	PEN: {
		symbol: 'S/.',
		name: 'Nuevo Sol',
		digitalCode: 604,
		code: 'PEN',
	},
	PGK: {
		symbol: 'K',
		name: 'Kina',
		digitalCode: 598,
		code: 'PGK',
	},
	PHP: {
		symbol: '₱',
		name: 'Philippine Peso',
		digitalCode: 608,
		code: 'PHP',
	},
	PKR: {
		symbol: '₨',
		name: 'Pakistan Rupee',
		digitalCode: 586,
		code: 'PKR',
	},
	PLN: {
		symbol: 'zł',
		name: 'PZloty',
		digitalCode: 985,
		code: 'PLN',
	},
	PYG: {
		symbol: '₲',
		name: 'Guarani',
		digitalCode: 600,
		code: 'PYG',
	},
	QAR: {
		symbol: 'ر.ق',
		name: 'Qatari Rial',
		digitalCode: 634,
		code: 'QAR',
	},
	RON: {
		symbol: 'L',
		name: 'Leu',
		digitalCode: 946,
		code: 'RON',
	},
	RSD: {
		symbol: 'din',
		name: 'Serbian Dinar',
		digitalCode: 941,
		code: 'RSD',
	},
	RUB: {
		symbol: 'р.',
		name: 'Russian Ruble',
		digitalCode: 643,
		code: 'RUB',
	},
	RWF: {
		symbol: '₣',
		name: 'Rwanda Franc',
		digitalCode: 646,
		code: 'RWF',
	},
	SAR: {
		symbol: 'ر.س',
		name: 'Saudi Riyal',
		digitalCode: 682,
		code: 'SAR',
	},
	SBD: {
		symbol: '$',
		name: 'Solomon Islands Dollar',
		digitalCode: 90,
		code: 'SBD',
	},
	SCR: {
		symbol: '₨',
		name: 'Seychelles Rupee',
		digitalCode: 690,
		code: 'SCR',
	},
	SDG: {
		symbol: '£',
		name: 'Sudanese Pound',
		digitalCode: 938,
		code: 'SDG',
	},
	SEK: {
		symbol: 'kr',
		name: 'Swedish Krona',
		digitalCode: 752,
		code: 'SEK',
	},
	SGD: {
		symbol: '$',
		name: 'Singapore Dollar',
		digitalCode: 702,
		code: 'SGD',
	},
	SHP: {
		symbol: '£',
		name: 'Saint Helena Pound',
		digitalCode: 654,
		code: 'SHP',
	},
	SLL: {
		symbol: 'Le',
		name: 'Leone',
		digitalCode: 694,
		code: 'SLL',
	},
	SOS: {
		symbol: 'Sh',
		name: 'Somali Shilling',
		digitalCode: 706,
		code: 'SOS',
	},
	SRD: {
		symbol: '$',
		name: 'Suriname Dollar',
		digitalCode: 968,
		code: 'SRD',
	},
	STD: {
		symbol: 'Db',
		name: 'Dobra',
		digitalCode: 678,
		code: 'STD',
	},
	SYP: {
		symbol: 'ل.س',
		name: 'Syrian Pound',
		digitalCode: 760,
		code: 'SYP',
	},
	SZL: {
		symbol: 'L',
		name: 'Lilangeni',
		digitalCode: 748,
		code: 'SZL',
	},
	THB: {
		symbol: '฿',
		name: 'Baht',
		digitalCode: 764,
		code: 'THB',
	},
	TJS: {
		symbol: 'ЅМ',
		name: 'Somoni',
		digitalCode: 972,
		code: 'TJS',
	},
	TMT: {
		symbol: 'm',
		name: 'Manat',
		digitalCode: 934,
		code: 'TMT',
	},
	TND: {
		symbol: 'د.ت',
		name: 'Tunisian Dinar',
		digitalCode: 788,
		code: 'TND',
	},
	TOP: {
		symbol: 'T$',
		name: 'Pa’anga',
		digitalCode: 776,
		code: 'TOP',
	},
	TRY: {
		symbol: '₤',
		name: 'Turkish Lira',
		digitalCode: 949,
		code: 'TRY',
	},
	TTD: {
		symbol: '$',
		name: 'Trinidad and Tobago Dollar',
		digitalCode: 780,
		code: 'TTD',
	},
	TWD: {
		symbol: '$',
		name: 'Taiwan Dollar',
		digitalCode: 901,
		code: 'TWD',
	},
	TZS: {
		symbol: 'Sh',
		name: 'Tanzanian Shilling',
		digitalCode: 834,
		code: 'TZS',
	},
	UAH: {
		symbol: '₴',
		name: 'Hryvnia',
		digitalCode: 980,
		code: 'UAH',
	},
	UGX: {
		symbol: 'Sh',
		name: 'Uganda Shilling',
		digitalCode: 800,
		code: 'UGX',
	},
	USD: {
		symbol: '$',
		name: 'US Dollar',
		digitalCode: 840,
		code: 'USD',
	},
	UYU: {
		symbol: '$',
		name: 'Peso Uruguayo',
		digitalCode: 858,
		code: 'UYU',
	},
	UZS: {
		symbol: 'so?m',
		name: 'Uzbekistan Sum',
		digitalCode: 860,
		code: 'UZS',
	},
	VEF: {
		symbol: 'Bs F',
		name: 'Bolivar Fuerte',
		digitalCode: 937,
		code: 'VEF',
	},
	VND: {
		symbol: '₫',
		name: 'Dong',
		digitalCode: 704,
		code: 'VND',
	},
	VUV: {
		symbol: 'Vt',
		name: 'Vatu',
		digitalCode: 548,
		code: 'VUV',
	},
	WST: {
		symbol: 'T',
		name: 'Tala',
		digitalCode: 882,
		code: 'WST',
	},
	XAF: {
		symbol: '₣',
		name: 'CFA Franc BCEAO',
		digitalCode: 950,
		code: 'XAF',
	},
	XCD: {
		symbol: '$',
		name: 'East Caribbean Dollar',
		digitalCode: 951,
		code: 'XCD',
	},
	XPF: {
		symbol: '₣',
		name: 'CFP Franc',
		digitalCode: 953,
		code: 'XPF',
	},
	YER: {
		symbol: '﷼',
		name: 'Yemeni Rial',
		digitalCode: 886,
		code: 'YER',
	},
	ZAR: {
		symbol: 'R',
		name: 'Rand',
		digitalCode: 710,
		code: 'ZAR',
	},
	ZMW: {
		symbol: 'ZK',
		name: 'Zambian Kwacha',
		digitalCode: 967,
		code: 'ZMW',
	},
	ZWL: {
		symbol: '$',
		name: 'Zimbabwe Dollar',
		digitalCode: 932,
		code: 'ZWL',
	},
};

export type CurrencyCode = (typeof currencyCodes)[number];
